import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Layout from "../components/layoutBlack";
import { removeFromCart } from "../store/slices/cartSlice.ts";
import { Formik, Form, Field, ErrorMessage, useFormikContext } from "formik";
import { config } from "../utils/constants";
import { US_STATES } from "../components/states.js";
import axios from "axios";
import * as Yup from "yup";
import countryData from "../components/countries.json"; // custom JSON data
import Select from "react-select";
import { showLoader, hideLoader } from "../store/slices/loader.ts";
import { setBoxData, setPriceData } from "../store/slices/cartSlice.ts"; 
import secureCheckoutImg from "../assets/images/icons/secure-checkout-png.png";



const CountrySelect = ({ fieldName }) => {
  const [countries] = useState(() => {
    const sorted = [
      ...countryData.countries.filter((c) => c.value === "US"),
      ...countryData.countries
        .filter((c) => c.value !== "US")
        .sort((a, b) => a.label.localeCompare(b.label)),
    ];
    return sorted;
  });

  const { values, setFieldValue } = useFormikContext();
  const value = countries.find((c) => c.value === values?.[fieldName.split(".")[0]]?.country);

  return (
    <Select
      name={fieldName}
      options={countries}
      value={value || null}
      onChange={(option) => setFieldValue(fieldName, option.value)}
      classNamePrefix="react-select"
      className="react-select-container"
      placeholder="Select Country"
    />
  );
};
const StateField = ({ prefix }) => {
  const { values } = useFormikContext();
  const country = values[prefix].country;

  return (
    country === "US" ? (
      <Field
        as="select"
        name={`${prefix}.state`}
        className="form-select"
        autoComplete="new-password"
      >
        <option value="">Select State</option>
        {US_STATES.map(({ name, abbreviation }) => (
          <option key={abbreviation} value={abbreviation}>
            {name}
          </option>
        ))}
      </Field>
    ) : (
      <Field
        name={`${prefix}.state`}
        className="form-control"
        placeholder="State / Province / Region"
      />
    )
  );
};


const ShippingInfo = () => {
  const navigate = useNavigate(); 
  const REACT_APP_API_URL = config.url.REACT_APP_API_URL;
  const dispatch = useDispatch();
  const cart = useSelector((state) => state.cart);
  const items = useSelector((state) => state.cart.items);
 
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountMessage, setDiscountMessage] = useState("");
  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const discountValue = (subtotal * discountAmount).toFixed(2);
  const total = (subtotal - discountValue).toFixed(2);

  const handleRemove = (id) => dispatch(removeFromCart(id));

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

  const handleSubmit = async (values, { setSubmitting }) => {
    dispatch(showLoader("Finding carriers..."));
    const skus = items.map((item) => item.sku);
    const address = values.shipping;
    const billing_address = billingSameAsShipping ? values.shipping : values.billing;
    try {
      const response = await axios.post(`${REACT_APP_API_URL}/get-shipping-info/`, {
        cart,
        skus,
        address,
      });
      dispatch(setPriceData({
        'subtotal': subtotal,
        'total_price': total, 
        'discount_code': discountCode, 
        'discount_amount': discountAmount, 
        'discount_value': discountValue,
      })
    );
      dispatch(setBoxData({
        'box_length_inches': response.data.package_info['L'], 
        'box_width_inches': response.data.package_info['W'], 
        'box_height_inches': response.data.package_info['H'], 
        'box_weight_ounces': response.data.package_info['ounces'],
      })
    );
      navigate("/choose-carrier", {
        state: {
          shippingRates: response.data,   // the dict keyed by object_id
          billingInfo: billing_address,           // billing or shipping address depending on toggle
          shippingInfo: address,
        },
      });
    } catch (error) {
      console.error("Error submitting order:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const applyDiscount = async () => {
    try {
      const res = await axios.post(`${REACT_APP_API_URL}/check-discount/`, {
        code: discountCode,
      });
      const discountDecimal = parseFloat(res.data.discount); // e.g. 0.15 for 15% off
      const discountMessage = res.data.message; 
      if (!isNaN(discountDecimal)) {
        setDiscountAmount(discountDecimal);
        setDiscountMessage(discountMessage);
      } else {
        setDiscountMessage(discountMessage);
        throw new Error("Invalid discount format");
      }
    } catch (err) {
      setDiscountAmount(0);
    }
  };

  const buildConditionalSchema = Yup.object({
    firstName: Yup.string().required("Required"),
    lastName: Yup.string().required("Required"),
    email: Yup.string().email("Invalid email").required("Required"),
    street1: Yup.string().required("Required"),
    street2: Yup.string(),
    city: Yup.string().required("Required"),
    country: Yup.string().required("Required"),
    state: Yup.string().when("country", {
      is: "US",
      then: () => Yup.string().required("Required"),
      otherwise: () => Yup.string().notRequired(),
    }),
    zip: Yup.string().when("country", {
      is: "US",
      then: () => Yup.string().required("Required"),
      otherwise: () => Yup.string().notRequired(),
    }),
  });

  const validationSchema = (billingSameAsShipping) =>
    Yup.object().shape({
      shipping: buildConditionalSchema,
      billing: billingSameAsShipping ? Yup.object().shape({}) : buildConditionalSchema,
    });


  const initialValues = {
    shipping: {
      firstName: "",
      lastName: "",
      email: "",
      street1: "",
      street2: "",
      city: "",
      state: "",
      zip: "",
      country: "US",
    },
    billing: {
      firstName: "",
      lastName: "",
      email: "",
      street1: "",
      street2: "",
      city: "",
      state: "",
      zip: "",
      country: "US",
    },
  };


  return (
    <Layout>
      <section className="full-page-section bg0 p-t-60 p-b-80">
        <div className="container">
          <div className="row isotope-grid">
            <section className="bg0 p-t-62 p-b-60">
              <div className="container">
              <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema(billingSameAsShipping)}
                  onSubmit={handleSubmit}
                >
                  {({ values }) => (
                    <Form className="row">
                      <div className="col-lg-5 mb-5">
                        <h4>Shopping Cart</h4>
                        <table className="table">
                          <thead className="table-light">
                            <tr>
                              <th>Product</th>
                              <th>Qty</th>
                              <th>Total</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.map((item) => (
                              <tr key={item.id}>
                                <td>{item.name}</td>
                                <td>{item.quantity}</td>
                                <td>${(item.price * item.quantity).toFixed(2)}</td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleRemove(item.id)}
                                  >
                                    Remove
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="mt-3">
                          {discountMessage && (
                            <div className={`alert ${discountAmount > 0 ? "alert-success" : "alert-danger"}`} role="alert">
                              {discountMessage}
                            </div>
                          )}
                          <label className="form-label">Discount Code</label>
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control"
                              value={discountCode}
                              onChange={(e) => setDiscountCode(e.target.value)}
                              placeholder="Enter discount code"
                            />
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={applyDiscount}
                            >
                              Apply
                            </button>
                          </div>
                        </div>
                        <div className="card-footer d-flex justify-content-between py-3">
                          <div>
                            <div><strong>Subtotal:</strong></div>
                            {discountAmount > 0 && <div><strong>Discount:</strong></div>}
                            <div><strong>Total:</strong></div>
                          </div>
                          <div className="text-end">
                            <div>${subtotal.toFixed(2)}</div>
                            {discountAmount > 0 && <div className="text-success">– ${discountValue}</div>}
                            <div><strong>${total}</strong></div>
                          </div>
                        </div>
                        <div className="col-9 d-none d-lg-block mt-4">
                          <button type="submit" className="btn btn-primary w-100">
                            Continue to Payment
                          </button>
                          <div className="mt-3 w-100 d-flex justify-content-center p-t-20" style={{ overflow: "hidden" }}>
                            <img
                              src={secureCheckoutImg}
                              alt="Guaranteed safe checkout"
                              style={{
                                display: "block",
                                width: "80%",
                                maxWidth: "80%",
                                height: "auto",
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-7">
                        <div className="card shadow-sm">
                          <div className="card-body">
                            <h4>Shipping Address</h4>
                            <div className="row">
                              {["firstName", "lastName", "email", "street1", "street2", "city", "zip"].map((field) => (
                                <div className="mb-3 col-md-6" key={`shipping.${field}`}>
                                  <label className="form-label">{field.replace(/([A-Z])/g, " $1")}</label>
                                  <Field name={`shipping.${field}`} className="form-control" />
                                  <ErrorMessage name={`shipping.${field}`} component="div" className="text-danger small" />
                                </div>
                              ))}
                              <div className="mb-3 col-md-6">
                                <label className="form-label">Country</label>
                                <CountrySelect fieldName="shipping.country" />
                                <ErrorMessage name="shipping.country" component="div" className="text-danger small" />
                              </div>
                              <div className="mb-3 col-md-6">
                                <label className="form-label">State</label>
                                <StateField prefix="shipping" />
                                <ErrorMessage name="shipping.state" component="div" className="text-danger small" />
                              </div>
                            </div>

                            <div className="form-check form-switch my-3">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={billingSameAsShipping}
                                onChange={() => setBillingSameAsShipping(!billingSameAsShipping)}
                                id="billingSwitch"
                              />
                              <label className="form-check-label" htmlFor="billingSwitch">
                                Billing same as shipping address
                              </label>
                            </div>

                            {!billingSameAsShipping && (
                              <>
                                <h4>Billing Address</h4>
                                <div className="row">
                                  {["firstName", "lastName", "email", "street1", "street2", "city", "zip"].map((field) => (
                                    <div className="mb-3 col-md-6" key={`billing.${field}`}>
                                      <label className="form-label">{field.replace(/([A-Z])/g, " $1")}</label>
                                      <Field name={`billing.${field}`} className="form-control" />
                                      <ErrorMessage name={`billing.${field}`} component="div" className="text-danger small" />
                                    </div>
                                  ))}
                                  <div className="mb-3 col-md-6">
                                    <label className="form-label">Country</label>
                                    <CountrySelect fieldName="billing.country" />
                                    <ErrorMessage name="billing.country" component="div" className="text-danger small" />
                                  </div>
                                  <div className="mb-3 col-md-6">
                                    <label className="form-label">State</label>
                                    <StateField prefix="billing" />
                                    <ErrorMessage name="billing.state" component="div" className="text-danger small" />
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="col-12 mt-auto d-lg-none">
                          <button type="submit" className="btn btn-primary w-100 mt-5">
                            Continue to Payment
                          </button>
                          <div className="mt-3 w-100 d-flex justify-content-center p-t-20" style={{ overflow: "hidden" }}>
                            <img
                              src={secureCheckoutImg}
                              alt="Guaranteed safe checkout"
                              style={{
                                display: "block",
                                width: "80%",
                                maxWidth: "80%",
                                height: "auto",
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </section>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ShippingInfo;
